import { useMonaco } from "@monaco-editor/react";
import { useCallback, useEffect, useRef } from "react";
import type { Position, editor } from "@promptlayer/ui/monaco-types";

import {
  getCompletionInsertRange,
  setCodeBlockJavaScriptCompilerOptions,
} from "../monaco-completion-utils";

import { LanguageType } from "@/components/CodeEditor/types";

export const useCodeMonacoCompletion = (language: LanguageType, sources: string[] = []) => {
  const monaco = useMonaco();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const completionProviderRef = useRef<any | null>(null);

  const configureMonaco = useCallback(() => {
    if (!monaco) {
      return;
    }

    if (completionProviderRef.current) {
      completionProviderRef.current.dispose();
      completionProviderRef.current = null;
    }

    switch (language) {
      case "PYTHON":
        completionProviderRef.current = monaco.languages.registerCompletionItemProvider("python", {
          provideCompletionItems: (model: editor.ITextModel, position: Position) => {
            const range = getCompletionInsertRange(model, position);
            return {
              suggestions: [
                {
                  label: "data",
                  kind: monaco.languages.CompletionItemKind.Variable,
                  insertText: "data",
                  detail: "Dictionary containing row data",
                  documentation: "The data object contains the row data for the current execution.",
                  range,
                },
                ...sources.map(name => ({
                  label: `data['${name}']`,
                  kind: monaco.languages.CompletionItemKind.Property,
                  insertText: `data['${name}']`,
                  detail: `Value of ${name} source`,
                  documentation: `Access the value of the ${name} source for the current row.`,
                  range,
                })),
              ],
            };
          },
        });
        break;
      case "JAVASCRIPT":
        setCodeBlockJavaScriptCompilerOptions(monaco);

        completionProviderRef.current = monaco.languages.registerCompletionItemProvider(
          "javascript",
          {
            provideCompletionItems: (model: editor.ITextModel, position: Position) => {
              const range = getCompletionInsertRange(model, position);
              return {
                suggestions: [
                  {
                    label: "data",
                    kind: monaco.languages.CompletionItemKind.Variable,
                    insertText: "data",
                    detail: "Dictionary containing row data",
                    documentation:
                      "The data object contains the row data for the current execution.",
                    range,
                  },
                  ...sources.map(name => ({
                    label: `data.${name}`,
                    kind: monaco.languages.CompletionItemKind.Property,
                    insertText: `data.${name}`,
                    detail: `Value of ${name} source`,
                    documentation: `Access the value of the ${name} source for the current row.`,
                    range,
                  })),
                ],
              };
            },
          },
        );
        break;
      default:
        break;
    }
  }, [monaco, language, sources]);

  useEffect(() => {
    configureMonaco();
    return () => {
      if (completionProviderRef.current) {
        completionProviderRef.current.dispose();
        completionProviderRef.current = null;
      }
    };
  }, [configureMonaco]);
};
